import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import Cookies from 'js-cookie';
import {
  ApiError,
  CommonService,
  RiderService,
  RiderShowPoints,
} from 'services/openapi';
import useApi from 'services/mavapi/useApi';
import isApiError from 'utils/isApiError';
import { useErrorHandlingSlice } from 'app/error/slice';
import { useDispatch } from 'react-redux';
import useRegistrationPageImageApi from 'admin/hooks/useRegistrationPageImageApi ';
import { formatImageUrl } from 'app/pages/LoginPage';
import CircularLoader from 'app/components/CircularLoader';

const bgImage =
  'https://images.unsplash.com/photo-1466495227171-d05d7e3ac2b3?q=80&w=1229&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D';

type Props = {};

const ShowPoints = (props: Props) => {
  const currentUserId = Cookies.get('userId');
  const { riderGetRiderShowPoints } = RiderService;
  const { commonGetCurrentSeason } = CommonService;
  const {
    loading: isImageLoading,
    getRegistrationPageImageByType,
    imageByType,
  } = useRegistrationPageImageApi();
  const { error: apiError, handleRequest } = useApi();
  const [, setError] = React.useState<ApiError | unknown>();
  const [showPoints, setShowPoints] = useState<RiderShowPoints[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const dispatch = useDispatch();
  const { actions } = useErrorHandlingSlice();

  function fetchShowPoints() {
    handleRequest(commonGetCurrentSeason())
      .then(seasonRes => {
        if (seasonRes && !isApiError(seasonRes)) {
          const seasonId = seasonRes.liveSeason?.id ?? '';
          return handleRequest(
            riderGetRiderShowPoints(currentUserId!, seasonId),
          );
        }
      })
      .then(res => {
        if (res && !isApiError(res)) {
          setShowPoints(res as RiderShowPoints[]);
        } else if (res) {
          dispatch(
            actions.dispatchToast({
              errorTxt: (res as any).description,
              show: true,
              errorType: 'error',
            }),
          );
        }
        setLoading(false);
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  }

  React.useEffect(() => {
    fetchShowPoints();
    getRegistrationPageImageByType(12);
  }, []);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <HeroWrapper>
      {isImageLoading && (
        <LoaderOverlay>
          <CircularLoader />
        </LoaderOverlay>
      )}
      {!isImageLoading && (
        <HeroBg
          src={
            imageByType?.imageUrl
              ? formatImageUrl(imageByType?.imageUrl)
              : bgImage
          }
          alt=""
        />
      )}
      <HeroOverlay />

      <TitleBlock>
        <p className="welcome">Season Overview</p>
        <h1>Shows &amp; Points</h1>
      </TitleBlock>

      <TableSection>
        {loading ? (
          <>
            {/* Desktop skeleton */}
            <TableCard>
              <SkeletonWrapper>
                {[...Array(5)].map((_, i) => (
                  <SkeletonRow key={i}>
                    <SkeletonCell $width="18%" />
                    <SkeletonCell $width="10%" />
                    <SkeletonCell $width="10%" />
                    <SkeletonCell $width="10%" />
                    <SkeletonCell $width="12%" />
                    <SkeletonCell $width="10%" />
                    <SkeletonCell $width="8%" />
                    <SkeletonCell $width="7%" />
                  </SkeletonRow>
                ))}
              </SkeletonWrapper>
            </TableCard>
            {/* Mobile skeleton */}
            <MobileCardGrid>
              {[...Array(4)].map((_, i) => (
                <MobileCard key={i} $cancelled={false}>
                  <SkeletonLine $width="60%" $height="16px" />
                  <SkeletonLine $width="40%" $height="12px" />
                  <MobileCardDivider />
                  <MobileCardRow>
                    <SkeletonLine $width="30%" $height="12px" />
                    <SkeletonLine $width="20%" $height="12px" />
                  </MobileCardRow>
                  <MobileCardRow>
                    <SkeletonLine $width="35%" $height="12px" />
                    <SkeletonLine $width="25%" $height="12px" />
                  </MobileCardRow>
                  <MobileCardRow>
                    <SkeletonLine $width="28%" $height="12px" />
                    <SkeletonLine $width="18%" $height="12px" />
                  </MobileCardRow>
                </MobileCard>
              ))}
            </MobileCardGrid>
          </>
        ) : showPoints.length === 0 ? (
          <TableCard>
            <EmptyState>
              No show points found for the current season.
            </EmptyState>
          </TableCard>
        ) : (
          <>
            {/* Desktop table */}
            <TableCard>
              <TableScrollWrapper>
                <Table>
                  <thead>
                    <tr>
                      <Th>Show Name</Th>
                      <Th>Show Date</Th>
                      <Th>City</Th>
                      <Th>Location</Th>
                      <Th>Category</Th>
                      <Th>Division</Th>
                      <Th>Class</Th>
                      <Th>Split #</Th>
                      <Th>Status</Th>
                      <Th align="right">Points</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {showPoints.map((row, idx) => (
                      <Tr
                        key={row.showRiderId ?? idx}
                        $cancelled={!!row.entryCancel}
                      >
                        <Td>
                          <ShowName>{row.showName || '—'}</ShowName>
                        </Td>
                        <Td>{formatDate(row.startDate)}</Td>
                        <Td>{row.city || '—'}</Td>
                        <Td>{row.locationName || '—'}</Td>
                        <Td>{row.category || '—'}</Td>
                        <Td>{row.division || '—'}</Td>
                        <Td>{row.class || '—'}</Td>
                        <Td>{row.splitNumber || '—'}</Td>
                        <Td>
                          {row.entryCancel ? (
                            <CancelledBadge>Cancelled</CancelledBadge>
                          ) : (
                            <ActiveBadge>Active</ActiveBadge>
                          )}
                        </Td>
                        <Td align="right">
                          <PointsValue>{row.allPoint ?? '—'}</PointsValue>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableScrollWrapper>
            </TableCard>

            {/* Mobile cards */}
            <MobileCardGrid>
              {showPoints.map((row, idx) => (
                <MobileCard
                  key={row.showRiderId ?? idx}
                  $cancelled={!!row.entryCancel}
                >
                  <MobileCardHeader>
                    <MobileShowName>{row.showName || '—'}</MobileShowName>
                    {row.entryCancel ? (
                      <CancelledBadge>Cancelled</CancelledBadge>
                    ) : (
                      <ActiveBadge>Active</ActiveBadge>
                    )}
                  </MobileCardHeader>
                  <MobileCardDate>
                    {formatDate(row.startDate)}
                    {row.city ? ` · ${row.city}` : ''}
                  </MobileCardDate>
                  <MobileCardDivider />
                  <MobileCardRow>
                    <MobileLabel>Category</MobileLabel>
                    <MobileValue>{row.category || '—'}</MobileValue>
                  </MobileCardRow>
                  <MobileCardRow>
                    <MobileLabel>Division</MobileLabel>
                    <MobileValue>{row.division || '—'}</MobileValue>
                  </MobileCardRow>
                  <MobileCardRow>
                    <MobileLabel>Class</MobileLabel>
                    <MobileValue>{row.class || '—'}</MobileValue>
                  </MobileCardRow>
                  {row.splitNumber && (
                    <MobileCardRow>
                      <MobileLabel>Split #</MobileLabel>
                      <MobileValue>{row.splitNumber}</MobileValue>
                    </MobileCardRow>
                  )}
                  <MobileCardDivider />
                  <MobilePointsRow>
                    <MobileLabel>Total Points</MobileLabel>
                    <MobilePointsBig>{row.allPoint ?? '—'}</MobilePointsBig>
                  </MobilePointsRow>
                </MobileCard>
              ))}
            </MobileCardGrid>
          </>
        )}
      </TableSection>
    </HeroWrapper>
  );
};

const LoaderOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
`;

const HeroWrapper = styled.div`
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow: auto;
  background: #1a1a1a;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    height: auto;
  }
`;

const HeroBg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.45) 0%,
    rgba(0, 0, 0, 0.75) 60%,
    rgba(0, 0, 0, 0.92) 100%
  );
`;

const TitleBlock = styled.div`
  position: relative;
  z-index: 2;
  padding: 48px 90px 0;

  .welcome {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.7);
    letter-spacing: 3px;
    margin: 0 0 6px;
    font-weight: 400;
    text-transform: uppercase;
  }

  h1 {
    font-family: 'Bebas Neue', 'Impact', sans-serif;
    font-size: 60px;
    font-weight: 400;
    color: #ffffff;
    line-height: 1;
    margin: 0;
    letter-spacing: 3px;
    text-transform: uppercase;
  }

  @media (max-width: 768px) {
    padding: 32px 20px 0;

    h1 {
      font-size: 38px;
    }
  }
`;

const TableSection = styled.div`
  position: relative;
  z-index: 2;
  flex: 1;
  padding: 24px 90px 32px;
  display: flex;
  flex-direction: column;
  min-height: 0;

  @media (max-width: 768px) {
    padding: 20px 16px 32px;
  }
`;

const TableCard = styled.div`
  background: rgba(0, 0, 0, 0.55);
  border-radius: 16px;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 768px) {
    display: none;
  }
`;

const TableScrollWrapper = styled.div`
  overflow: auto;
  flex: 1;
  ::-webkit-scrollbar {
    width: 4px;
    height: 4px;
  }
  ::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.04);
  }
  ::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.18);
    border-radius: 4px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
`;

const Th = styled.th<{ align?: string }>`
  padding: 14px 16px;
  text-align: ${({ align }) => align || 'left'};
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  white-space: nowrap;
  position: sticky;
  top: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1;
`;

const Tr = styled.tr<{ $cancelled?: boolean }>`
  opacity: ${({ $cancelled }) => ($cancelled ? 0.5 : 1)};
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }

  &:not(:last-child) td {
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }
`;

const Td = styled.td<{ align?: string }>`
  padding: 13px 16px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  text-align: ${({ align }) => align || 'left'};
  white-space: nowrap;
`;

const ShowName = styled.span`
  font-weight: 600;
  color: #ffffff;
`;

const PointsValue = styled.span`
  font-weight: 700;
  font-size: 14px;
  color: #ffffff;
`;

const CancelledBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
  border: 1px solid rgba(239, 68, 68, 0.3);
`;

const ActiveBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(34, 197, 94, 0.15);
  color: #4ade80;
  border: 1px solid rgba(34, 197, 94, 0.25);
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 60px 20px;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.4);
`;

const MobileCardGrid = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
`;

const MobileCard = styled.div<{ $cancelled?: boolean }>`
  background: rgba(0, 0, 0, 0.55);
  border-radius: 14px;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 16px;
  opacity: ${({ $cancelled }) => ($cancelled ? 0.6 : 1)};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MobileCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const MobileShowName = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #ffffff;
  flex: 1;
  line-height: 1.3;
`;

const MobileCardDate = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  margin: 0;
`;

const MobileCardDivider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.07);
  margin: 2px 0;
`;

const MobileCardRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const MobileLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const MobileValue = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  text-align: right;
`;

const MobilePointsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const MobilePointsBig = styled.span`
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
`;

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: calc(400px + 100%) 0; }
`;

const SkeletonLine = styled.span<{ $width?: string; $height?: string }>`
  display: block;
  width: ${({ $width }) => $width || '80%'};
  height: ${({ $height }) => $height || '14px'};
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.06) 25%,
    rgba(255, 255, 255, 0.14) 50%,
    rgba(255, 255, 255, 0.06) 75%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s infinite linear;
`;

const SkeletonWrapper = styled.div`
  padding: 8px 0;
`;

const SkeletonRow = styled.div`
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const SkeletonCell = styled.span<{ $width?: string }>`
  display: block;
  width: ${({ $width }) => $width || '80%'};
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.06) 25%,
    rgba(255, 255, 255, 0.14) 50%,
    rgba(255, 255, 255, 0.06) 75%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s infinite linear;
  flex-shrink: 0;
`;

export default ShowPoints;
